import {
  tap,
  combineLatest,
  BehaviorSubject,
  Subscription,
  filter,
} from 'rxjs';
import {
  Component,
  OnInit,
  ComponentRef,
  SimpleChanges,
  SimpleChange,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute } from '@angular/router';

import { IIdentity, GridApi, GridSync } from '@grid/models';
import { DefaultSubMenuItems } from '@dpr-details/enums';
import {
  DprAttachmentsComponent,
  DprCommentComponent,
} from '@dpr-details/components';
import { RegistrationsComponent } from './registrations-component.model';
import { DprDetailsGridContainerBaseComponent } from './dpr-details-grid-container-base-component.model';
import { StaticMessage } from '@shared/enums';
import { DprAttachment, DprDetailsSync } from '@shared/models/dpr';

import { DprAttachmentsGridData } from '@shared/models/grid';
import { DprAttachmentService } from '@shared/services/dpr';

@UntilDestroy()
@Component({
  template: '',
})
export abstract class DprDetailsPageBaseComponent<R extends IIdentity>
  extends DprDetailsGridContainerBaseComponent<R>
  implements OnInit
{
  registrationsGridData!: R[];
  registrationsChanges!: GridSync<R>;
  registrationsGridApi!: GridApi<R>;
  attachmentsGridData!: DprAttachmentsGridData[];
  attachmentsChanges!: GridSync<DprAttachmentsGridData>;
  attachmentsGridApi!: GridApi<DprAttachmentsGridData>;
  comment!: string;
  DefaultSubMenuItems = DefaultSubMenuItems;
  StaticMessage = StaticMessage;

  protected identifier: string;
  protected shouldDisplayPrintInReport = true;

  protected activatedComponent$ = new BehaviorSubject<ComponentRef<any>>(null);

  private registrationChangesSub!: Subscription;
  private registrationGridRegisteredSub!: Subscription;

  private attachmentsChangesSub!: Subscription;
  private attachmentsGridRegisteredSub!: Subscription;

  private commentChangesSub: Subscription;

  private dataReady$ = new BehaviorSubject<boolean>(false);
  private gridsOfflineChangesReady$ = new BehaviorSubject<boolean>(false);

  protected abstract labeling: { [key: string]: string };

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected dprAttachmentService: DprAttachmentService
  ) {
    super(activatedRoute);

    this.unsavedChangesService.register(this.save.bind(this), this.isDirty$);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.dprDetailsOfflineChanges$
      .pipe(
        tap((dprDetailsOfflineChanges) => {
          // timeout is needed to execute this after the grid changes are cleared following a successful save
          setTimeout(() => {
            this.buildGridsChanges(dprDetailsOfflineChanges);
            this.gridsOfflineChangesReady$.next(true);
          });
        })
      )
      .subscribe();

    combineLatest([
      this.gridsOfflineChangesReady$,
      this.dprDetails$,
      this.selectedSubproject$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(([gridsOfflineChangesReady]) => !!gridsOfflineChangesReady),
        tap(() => {
          this.setupData();
          this.dataReady$.next(true);
        })
      )
      .subscribe();

    combineLatest([
      this.activatedComponent$,
      this.dataReady$,
      this.dprDetailsOfflineChanges$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(
          ([activatedComponent, dataReady]) => !!activatedComponent && dataReady
        ),
        tap(([event]) => {
          this.onActivatedComponentChanged(event);
        })
      )
      .subscribe();
  }

  //#region Routing

  onComponentActivated(component: ComponentRef<any>): void {
    this.activatedComponent$.next(component);
  }

  //#endregion

  protected refreshData(): void {
    const component = this.activatedComponent$.value;
    if (this.isRegistrationComponent(component)) {
      (component as unknown as RegistrationsComponent<R>)?.api?.reset();
    }

    // auto-fill logic stores registration grid changes also when the comment tab is active, so we need to clear them
    if (component instanceof DprCommentComponent) {
      this.registrationsGridApi?.reset();
    }

    if (component instanceof DprAttachmentsComponent) {
      this.attachmentsGridApi?.reset();
    }

    this.registrationsChanges = null;
    this.attachmentsChanges = null;

    this.isDirty$.next(false);
  }

  /**
   * Locally stores the changes and the data of the given component
   * with the scope of restore them later when navigating back to that component
   */
  protected storeComponentChanges(component: ComponentRef<any>): void {
    if (!component) {
      return;
    }

    if (this.isRegistrationComponent(component)) {
      this.storeRegistrationComponentChanges(component);
    }

    if (component instanceof DprCommentComponent) {
      this.storeCommentComponentChanges(component);
    }

    if (component instanceof DprAttachmentsComponent) {
      this.storeAttachmentComponentChanges(component);
    }
  }

  protected storeRegistrationComponentChanges(
    component: ComponentRef<any>
  ): void {
    this.registrationsChanges = (
      component as unknown as RegistrationsComponent<R>
    )?.api?.getChanges();
    this.registrationsGridData = (
      component as unknown as RegistrationsComponent<R>
    ).gridData;
  }

  protected storeCommentComponentChanges(component: DprCommentComponent): void {
    this.comment = component.commentFormControl.value?.trim();
  }

  protected storeAttachmentComponentChanges(
    component: DprAttachmentsComponent
  ): void {
    this.attachmentsChanges = component.dprAttachmentsGrid?.api?.getChanges();
    this.attachmentsGridData = component.dprAttachmentsGrid.gridData;
  }

  protected getOfflineGridDataChanges<T extends IIdentity>(
    offlineGridData: R[],
    offlineChanges: GridSync<T>
  ): GridSync<R> {
    return {
      addedList: offlineGridData.filter((x) =>
        offlineChanges.addedList.some((a) => a.id === x.id)
      ),
      updatedList: offlineGridData.filter((x) =>
        offlineChanges.updatedList.some((u) => u.id === x.id)
      ),
      deletedIds: offlineChanges.deletedIds,
    };
  }

  protected getOfflineAttachmentGridDataChanges(
    offlineChanges: GridSync<DprAttachment>
  ): GridSync<DprAttachmentsGridData> {
    return {
      addedList: this.dprAttachmentService.mapToGridData(
        offlineChanges?.addedList
      ),
      updatedList: this.dprAttachmentService.mapToGridData(
        offlineChanges?.updatedList
      ),
      deletedIds: offlineChanges?.deletedIds,
    };
  }

  protected resetGrids(): void {
    this.registrationsChanges = null;
    this.attachmentsChanges = null;
    this.attachmentsGridApi?.reset();
    this.registrationsGridApi?.reset();
  }

  //#region Private methods

  private onActivatedComponentChanged(component: ComponentRef<any>): void {
    if (component !== this.activatedComponent) {
      this.storeComponentChanges(this.activatedComponent);
    }

    this.setupRouteComponent(component);

    this.activatedComponent = component;
  }

  /**
   *
   * @description initializes the given component with the right inputs
   * Inputs are read from the local data and changes
   */
  private setupRouteComponent(component: ComponentRef<any>): void {
    if (!component) {
      return;
    }

    if (this.isRegistrationComponent(component)) {
      this.setupRegistrations(
        component as unknown as RegistrationsComponent<R>
      );
    }

    if (component instanceof DprCommentComponent) {
      this.setupComment(component);
    }

    if (component instanceof DprAttachmentsComponent) {
      this.setupAttachments(component);
    }

    this.setupCanEditBinding();
    this.setupIsAppOnlineBinding();
  }

  private setupRegistrations(component: RegistrationsComponent<R>): void {
    component.gridData = this.registrationsGridData;
    component.predefinedChanges = this.registrationsChanges;
    component.selectedSubproject = this.selectedSubproject;
    component.canEdit = this.canEdit;
    component.labeling = this.labeling;

    const changes: SimpleChanges = {
      gridData: new SimpleChange(null, this.registrationsGridData, true),
      predefinedChanges: new SimpleChange(
        null,
        this.registrationsChanges,
        true
      ),
      selectedSubproject: new SimpleChange(null, this.selectedSubproject, true),
      canEdit: new SimpleChange(null, this.canEdit, true),
      labeling: new SimpleChange(null, this.labeling, true),
    };

    component.ngOnChanges(changes);

    this.registrationChangesSub?.unsubscribe();
    this.registrationChangesSub = component.dataChanged
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isDirty$.next(true);
      });

    this.registrationGridRegisteredSub?.unsubscribe();
    this.registrationGridRegisteredSub = component.gridRegistered
      .pipe(untilDestroyed(this))
      .subscribe((gridApi) => {
        this.registrationsGridApi = gridApi;
      });
  }

  private setupComment(component: DprCommentComponent): void {
    component.comment = this.comment;
    component.canEdit = this.canEdit;

    const changes: SimpleChanges = {
      comment: new SimpleChange(null, this.comment, true),
      canEdit: new SimpleChange(null, this.canEdit, true),
    };

    component.ngOnChanges(changes);

    this.commentChangesSub?.unsubscribe();
    this.commentChangesSub = component.commentChanged
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isDirty$.next(true);
      });
  }

  private setupAttachments(component: DprAttachmentsComponent): void {
    const namingConvention = this.selectedSubproject?.data?.namingConvention;

    component.gridData = this.attachmentsGridData;
    component.predefinedChanges = this.attachmentsChanges;
    component.namingConvention = namingConvention;
    component.identifier = this.identifier;
    component.canEdit = this.canEdit;
    component.clientId = this.selectedClientId;
    component.isAppOnline = this.isAppOnline;
    component.shouldDisplayPrintInReport = this.shouldDisplayPrintInReport;

    const changes: SimpleChanges = {
      gridData: new SimpleChange(null, this.attachmentsGridData, true),
      predefinedChanges: new SimpleChange(null, this.attachmentsChanges, true),
      namingConvention: new SimpleChange(null, namingConvention, true),
      identifier: new SimpleChange(null, this.identifier, true),
      canEdit: new SimpleChange(null, this.canEdit, true),
      clientId: new SimpleChange(null, this.selectedClientId, true),
      isAppOnline: new SimpleChange(null, this.isAppOnline, true),
      displayPrintInReport: new SimpleChange(
        null,
        this.shouldDisplayPrintInReport,
        true
      ),
    };

    component.ngOnChanges(changes);

    this.attachmentsChangesSub?.unsubscribe();
    this.attachmentsChangesSub = component.dataChanged
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isDirty$.next(true);
      });

    this.attachmentsGridRegisteredSub?.unsubscribe();
    this.attachmentsGridRegisteredSub = component.gridRegistered
      .pipe(untilDestroyed(this))
      .subscribe((gridApi) => {
        this.attachmentsGridApi = gridApi;
      });
  }

  /**
   * @description Initializes the grid changes with the data saved in offline
   */
  private buildGridsChanges(dprDetailsOfflineChanges: DprDetailsSync): void {
    this.registrationsChanges = this.getOfflineRegistrationsChanges(
      dprDetailsOfflineChanges
    );
    this.attachmentsChanges = this.getOfflineAttachmentsChanges(
      dprDetailsOfflineChanges
    );
  }

  //#endregion

  //#region Abstract methods

  protected abstract setupData(): void;

  protected abstract isRegistrationComponent(
    component: ComponentRef<any>
  ): boolean;

  protected abstract getOfflineRegistrationsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<R>;

  protected abstract getOfflineAttachmentsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprAttachmentsGridData>;

  //#endregion
}
